// Notifications — Office insight. The messages addressed to the person
// looking at the screen, and the four things they can do with them.
//
// ── The contract, read off server/routes/notifications.js ───────────────────
//
//   GET    /api/notifications                 the caller's rows, newest first,
//                                             `LIMIT 100`. Accepts ONE query
//                                             param, `is_read` — the string
//                                             'true' means read, anything else
//                                             (including 'false') means unread,
//                                             and omitting it means both.
//   GET    /api/notifications/unread-count    → { count }
//   PUT    /api/notifications/:id/read        → the updated row, or 404
//   PUT    /api/notifications/read-all        → { message }
//   DELETE /api/notifications/:id             → { message }, or 404
//   DELETE /api/notifications/clear-all       → { message }
//
// `SELECT *` on the `notifications` table, so a row is exactly its columns
// (server/database.js:405): id, user_id, title, message, type, is_read, link,
// created_at. Nothing else exists — every field rendered below is one of those,
// and NotificationContract.test asserts each against the CREATE TABLE.
//
// **read-all and mark-all-read are BOTH live and do the identical UPDATE.**
// The route file registers `put('/read-all')` and, forty lines further down,
// `put('/mark-all-read')` commented "Alias for mark-all-read (frontend
// compatibility)" — the classic screen
// (client/src/components/Notifications/Notifications.js:141) calls the alias,
// which is why it exists. Neither is dead: `/:id/read` is registered above both
// and needs TWO path segments, so it cannot swallow a one-segment path. Pinned
// server-side in server/utils/__tests__/notificationRoutes.test.js:264-292.
// This screen calls `/read-all`, the canonical one.
//
// ── Nothing here gates on a permission, deliberately ───────────────────────
//
// Every route above carries `authMiddleware` and nothing else; each query is
// scoped `WHERE user_id = ?`. Only `POST /` (which this screen never calls)
// adds `checkRole('admin', 'manager')`. So a worker may read, mark and clear
// their OWN notifications — and they get them: server/routes/field.js notifies
// on a problem report and a job claim, and routes/timesheets.js notifies the
// worker when a sheet is approved or rejected.
//
// `useCan('notifications', 'update')` would therefore be WRONG here. The
// worker baseline is `notifications: ['read']` (server/database.js:1061), so
// gating on it would hide "Mark all read" and "Clear all" from someone the
// server would have obeyed — a ghost refusal, the mirror image of a ghost
// button. The contract test asserts none of the six handlers has grown a role
// check, so if the server ever does gate them, this comment fails rather than
// rots.

import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { put, del } from '../../shared/http';
import { Pill, FwButton, Skeleton, EmptyState } from '../../../ui/fw';
import { Icon } from '../../../ui';
import { fmtDateTime } from '../shared/format';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, Panel, LoadError, FormError, ConfirmSheet,
} from '../records/OfficePage';
import './Notifications.css';

// ── the contract, as data ───────────────────────────────────────────────────

/**
 * Every path this screen calls. A table rather than six string literals
 * scattered through the JSX, so NotificationContract.test can check each one
 * against the route file instead of a screen offering a button the server
 * answers 404 to (CLAUDE.md: "Declare a contract once, and check it against
 * the server").
 */
export const NOTIFICATION_PATHS = {
  list: '/notifications',
  unreadCount: '/notifications/unread-count',
  markOneRead: (id) => `/notifications/${id}/read`,
  markAllRead: '/notifications/read-all',
  deleteOne: (id) => `/notifications/${id}`,
  clearAll: '/notifications/clear-all',
};

/** The react-query prefix every notification query sits under. */
export const NOTIFICATIONS_KEY = ['notifications'];
/**
 * The unread-count query's own key — THIS SCREEN's count, not the nav bell's.
 *
 * The comment here used to read "A shell bell watches this one", which was
 * false in a way that mattered: `shell/badges.js:145` runs its own query keyed
 * ['badges','unread-notifications'], and neither key is a prefix of the other.
 * Invalidating this one does not move the badge. `shell/liveUpdates.js` names
 * both, which is the only place that knows they are two.
 */
export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'];

/**
 * The server caps `GET /notifications` at `LIMIT 100`. A screen that shows 100
 * of 340 rows and says "100 notifications" is stating a total it did not
 * measure, so the header says "the most recent 100" once the list is full.
 */
export const LIST_LIMIT = 100;

/**
 * The three filters, and the query string each one sends.
 *
 * The server reads `is_read === 'true' ? 1 : 0`, so 'false' and 'no' and a
 * typo all mean unread. `false` is spelled out anyway because it is what the
 * value means to a person reading a request log.
 */
export const FILTERS = [
  { key: 'all', label: 'All', qs: '' },
  { key: 'unread', label: 'Unread', qs: '?is_read=false' },
  { key: 'read', label: 'Read', qs: '?is_read=true' },
];

/**
 * The types the server actually writes, and how each is drawn.
 *
 * Four of them come from `POST /`'s validator
 * (`isIn(['info', 'warning', 'success', 'error'])`); the other four are written
 * by name — 'timesheet' (routes/timesheets.js and routes/notifications.js
 * /timesheet-submitted), 'job_pause' (/job-paused), and 'problem' and 'job'
 * (routes/field.js, the two a crew member triggers).
 *
 * Icon names are from client/src/ui/components/Icon.js; an invalid one renders
 * nothing, and iconNames.test.js checks every name in the tree.
 */
const TYPE_META = {
  problem:    { icon: 'alertTriangle', tone: 'stop', label: 'Problem' },
  job:        { icon: 'jobs', tone: 'sched', label: 'Job' },
  job_pause:  { icon: 'pause', tone: 'warn', label: 'Job paused' },
  timesheet:  { icon: 'document', tone: 'sched', label: 'Timesheet' },
  info:       { icon: 'info', tone: 'neutral', label: 'Info' },
  warning:    { icon: 'alertTriangle', tone: 'warn', label: 'Warning' },
  success:    { icon: 'check', tone: 'ok', label: 'Done' },
  error:      { icon: 'alert', tone: 'stop', label: 'Problem' },
};

/** Exported for the contract test, which checks this covers what the server sends. */
export const KNOWN_TYPES = Object.keys(TYPE_META);

/** An Icon name for a row. Falls back to the bell for a type nobody mapped. */
export function iconForType(type) {
  const meta = TYPE_META[String(type || '').trim()];
  return meta ? meta.icon : 'bell';
}

/** A Pill tone for a row. */
export function toneForType(type) {
  const meta = TYPE_META[String(type || '').trim()];
  return meta ? meta.tone : 'neutral';
}

/**
 * What the type pill says.
 *
 * An unmapped type is HUMANISED rather than hidden ('invoice_overdue' →
 * 'Invoice overdue'), so a type added on the server shows up on screen as
 * itself instead of degrading silently into a generic bell with no label. That
 * is the difference between "we do not draw this one specially yet" and "this
 * row is a mystery".
 */
export function labelForType(type) {
  const key = String(type || '').trim();
  if (!key) return 'Notice';
  if (TYPE_META[key]) return TYPE_META[key].label;
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Is this row unread?
 *
 * `is_read` is an INTEGER column with DEFAULT 0, so it arrives as 0 or 1 — but
 * the classic screen already had to cope with `true`/`false` from an optimistic
 * update, and a row echoed straight back from `PUT /:id/read` is whatever
 * better-sqlite3 handed over. Truthiness is the only test that is right for all
 * of them, and `!n.is_read` on a row that has no `is_read` at all would call it
 * unread — so an absent column is treated as read rather than as an alarm.
 */
export function isUnread(row) {
  if (!row || !('is_read' in row)) return false;
  return !row.is_read;
}

/**
 * A notification's link, but only when it is somewhere in this app.
 *
 * The links the server writes are in-app paths ('/timesheets', '/jobs',
 * '/now' — routes/field.js and routes/timesheets.js) and RebuildRouter
 * redirects each to whichever app owns it for the role. But `link` on
 * `POST /notifications` is a free-text body field with no validator at all, so
 * anything can be in there. `//evil.example` is a protocol-relative URL that a
 * router would send off-site, and 'javascript:' does what it says, so this
 * returns a path or nothing.
 */
export function inAppLink(link) {
  const s = typeof link === 'string' ? link.trim() : '';
  if (!s.startsWith('/') || s.startsWith('//')) return null;
  return s;
}

/**
 * The line under the title. Every number in it was measured, or it is not there.
 *
 * A count reads as arithmetic and arithmetic is trusted (docs/AGENT_LEARNINGS.md,
 * 2026-08-17), so "0 unread" from a request that never answered is worse than
 * saying nothing: it is the app telling someone there is nothing waiting for
 * them. `shown` and `unread` are null until their request came back, and a
 * failed unread count says so in words rather than resolving to a number.
 */
export function headerMeta({ shown = null, unread = null, unreadFailed = false, capped = false } = {}) {
  const parts = [];
  if (Number.isFinite(shown)) {
    parts.push(capped ? `the most recent ${LIST_LIMIT}` : `${shown} ${shown === 1 ? 'notification' : 'notifications'}`);
  }
  if (Number.isFinite(unread)) parts.push(`${unread} unread`);
  else if (unreadFailed) parts.push('unread count unavailable');
  return parts.length ? parts.join(' · ') : null;
}

/**
 * What a bell shows next to itself. Null when there is nothing to show — an
 * unmeasured count and a genuine zero both mean "no badge", and neither means
 * "0".
 */
export function unreadBadge(count, max = 99) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > max ? `${max}+` : String(n);
}

/**
 * The unread count, for a shell that wants a bell.
 *
 * `count` is null until the number arrived — never 0 as a stand-in — so a
 * caller can tell "nothing waiting" from "nobody answered". Exported from here
 * rather than reimplemented in three shells so the badge and this screen cannot
 * disagree about which endpoint is authoritative.
 */
export function useUnreadCount() {
  const q = useOfficeQuery(UNREAD_COUNT_KEY, NOTIFICATION_PATHS.unreadCount);
  const count = q.data && Number.isFinite(Number(q.data.count)) ? Number(q.data.count) : null;
  return { count, error: q.error || null, isPending: q.isPending, badge: unreadBadge(count) };
}

// ── the screen ──────────────────────────────────────────────────────────────

function NotificationRow({ row, busy, onMarkRead, onDelete }) {
  const unread = isUnread(row);
  const to = inAppLink(row.link);
  return (
    <li className={['or-notif__item', unread ? 'or-notif__item--unread' : 'or-notif__item--read'].join(' ')}>
      <span className="or-notif__icon" aria-hidden="true"><Icon name={iconForType(row.type)} /></span>
      <div className="or-notif__body">
        <div className="or-notif__head">
          <span className="or-notif__title">{row.title}</span>
          <Pill tone={toneForType(row.type)}>{labelForType(row.type)}</Pill>
          {unread ? <Pill tone="now">Unread</Pill> : null}
        </div>
        {row.message ? <p className="or-notif__message">{row.message}</p> : null}
        <div className="or-notif__meta">
          <span>{fmtDateTime(row.created_at)}</span>
          {to ? <Link className="or-link or-notif__link" to={to}>Open<Icon name="arrowRight" /></Link> : null}
        </div>
      </div>
      <div className="or-notif__actions">
        {unread ? (
          <FwButton variant="ghost" icon="check" onClick={() => onMarkRead(row.id)} disabled={busy}>
            Mark read
          </FwButton>
        ) : null}
        <FwButton
          variant="quiet"
          icon="trash"
          onClick={() => onDelete(row.id)}
          disabled={busy}
          aria-label={`Delete notification: ${row.title || 'untitled'}`}
        />
      </div>
    </li>
  );
}

export default function Notifications() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [confirmClear, setConfirmClear] = useState(false);
  const { busy, error: writeError, run } = useAction();

  const qs = (FILTERS.find((f) => f.key === filter) || FILTERS[0]).qs;
  const listPath = `${NOTIFICATION_PATHS.list}${qs}`;
  const listQ = useOfficeQuery([...NOTIFICATIONS_KEY, 'list', listPath], listPath);
  const unreadQ = useUnreadCount();

  const rows = useMemo(() => (Array.isArray(listQ.data) ? listQ.data : []), [listQ.data]);
  // `listFailed` is the whole reason this screen can be trusted. react-query
  // keeps the last good `data` when a refetch fails, so "did it fail" and "is
  // there anything to show" are separate questions and the empty state below
  // asks BOTH. A 403 that renders as "Nothing here" is the single
  // most-repeated defect in this repo.
  const listFailed = listQ.error || null;
  const measured = !listFailed && !listQ.isPending && Array.isArray(listQ.data);

  /**
   * Both queries, always — on success AND on refusal.
   *
   * A refused mark-all-read may have applied to some rows before it stopped
   * (the classic screen's `writeFailed` says the same, Notifications.js:105),
   * so the only safe thing to believe afterwards is the server. `NOTIFICATIONS_KEY`
   * is a prefix, so this covers all three filters and the unread count.
   *
   * The `notifications-changed` event is the second half, and it was MISSING.
   * `shell/badges.js:156` has always listened for it — its own comment said
   * "the Notifications screen fires `notifications-changed` when it marks
   * anything read" — and nothing in the tree ever dispatched it
   * (`grep -rn "notifications-changed" client/src` found the listener and no
   * dispatcher). The shell bell keys its query ['badges','unread-notifications']
   * while everything here sits under ['notifications'], and neither is a prefix
   * of the other, so invalidating locally left the NAV BELL showing the old
   * count until its 60s poll came round. Marking everything read and watching
   * the badge keep its number is the visible half of that.
   */
  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    window.dispatchEvent(new Event('notifications-changed'));
  }, [qc]);

  const act = useCallback(async (fn, fallback) => {
    const ok = await run(fn, fallback);
    await refresh();
    return ok;
  }, [run, refresh]);

  const markOneRead = (id) => act(() => put(NOTIFICATION_PATHS.markOneRead(id)), 'Could not mark that as read.');
  const deleteOne = (id) => act(() => del(NOTIFICATION_PATHS.deleteOne(id)), 'Could not delete that notification.');
  const markAllRead = () => act(() => put(NOTIFICATION_PATHS.markAllRead), 'Could not mark everything as read.');
  const clearAll = async () => {
    const ok = await act(() => del(NOTIFICATION_PATHS.clearAll), 'Could not clear your notifications.');
    if (ok) setConfirmClear(false);
  };

  const meta = headerMeta({
    shown: measured ? rows.length : null,
    unread: unreadQ.count,
    unreadFailed: !!unreadQ.error,
    capped: measured && rows.length >= LIST_LIMIT,
  });

  /*
    Disabled means "there is nothing to do", which is a claim about the data.
    So neither button may be disabled by a request that FAILED:

      Mark all read  — off only when the unread count came back as 0.
                       `unreadQ.count` is null while unmeasured, and
                       `null === 0` is false, so a refused count leaves the
                       button live and the server gets to answer.
      Clear all      — off only when the unfiltered list came back empty.
                       Under the Unread filter an empty list says nothing
                       about whether there are read rows to clear.
  */
  const nothingUnread = unreadQ.count === 0;
  const nothingAtAll = filter === 'all' && measured && rows.length === 0;

  return (
    <div className="or-page" data-screen="office-Notifications">
      <PageHeader title="Notifications" meta={meta}>
        <FwButton variant="ghost" icon="check" onClick={markAllRead} loading={busy} disabled={nothingUnread}>
          Mark all read
        </FwButton>
        <FwButton variant="stop" icon="trash" onClick={() => setConfirmClear(true)} disabled={busy || nothingAtAll}>
          Clear all
        </FwButton>
      </PageHeader>

      <FormError error={writeError} />

      <Toolbar>
        <div className="or-seg" role="group" aria-label="Filter notifications">
          {FILTERS.map((f) => (
            <FwButton
              key={f.key}
              variant={filter === f.key ? 'primary' : 'ghost'}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </FwButton>
          ))}
        </div>
      </Toolbar>

      <Panel className="or-panel--flush">
        <div className="or-notif">
          {listQ.isPending ? <Skeleton lines={4} /> : null}
          {listFailed ? <LoadError error={listFailed} fallback="Could not load your notifications." /> : null}
          {measured && rows.length === 0 ? (
            <EmptyState icon="bell" title="Nothing here">
              {filter === 'unread' ? 'Everything addressed to you has been read.'
                : filter === 'read' ? 'Nothing has been marked as read yet.'
                  : 'When a crew reports a problem or a timesheet needs you, it shows up here.'}
            </EmptyState>
          ) : null}
          {rows.length > 0 ? (
            <ul className="or-notif__list">
              {rows.map((row) => (
                <NotificationRow
                  key={row.id}
                  row={row}
                  busy={busy}
                  onMarkRead={markOneRead}
                  onDelete={deleteOne}
                />
              ))}
            </ul>
          ) : null}
        </div>
      </Panel>

      <ConfirmSheet
        open={confirmClear}
        title="Clear every notification?"
        confirmLabel="Clear all notifications"
        busy={busy}
        onConfirm={clearAll}
        onClose={() => setConfirmClear(false)}
      >
        <p>
          This deletes every notification addressed to you, read or not. It cannot be undone, and
          it clears yours only — nobody else loses theirs.
        </p>
        <p>
          The work each one refers to is untouched: a reported problem stays on its job, a
          timesheet still needs approving.
        </p>
      </ConfirmSheet>
    </div>
  );
}
